import { Schema } from "effect";
import {
  ApiEnvironmentIdSchema,
  ApiProjectIdSchema,
  ArtifactIdSchema,
  ArtifactRevisionIdSchema,
  EnvironmentIdSchema,
  ProjectIdSchema,
  RequestIdSchema,
  ThreadIdSchema,
  WorkspaceIdSchema,
} from "./ids";
import {
  OfficeArtifactKindSchema,
  OfficeCanonicalIdSchema,
  OfficeDocumentTableIndexSchema,
  OfficeSelectionTargetSchema,
  type OfficeArtifactKind,
} from "./office";
import {
  HttpUrlSchema,
  IanaTimeZoneSchema,
  IsoDateTimeSchema,
  CanonicalIdentifierSchema,
  PapersFlowBoundaryParseOptions,
} from "./standard";
import { utf8ByteLength } from "./utf8";
import { ActorSchema } from "./workspace";

const BoundedLabelSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(240));
const BoundedCapabilitySchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80));
export const APP_CONTEXT_SELECTION_MAX_BYTES = 8 * 1_024;
export const APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES = 8 * 1_024;
export const APP_CONTEXT_NOTE_BODY_MAX_BYTES = 8 * 1_024;
const SelectionTextSchema = Schema.String.check(
  Schema.isMaxLength(APP_CONTEXT_SELECTION_MAX_BYTES),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= APP_CONTEXT_SELECTION_MAX_BYTES ||
      `selection text must not exceed ${APP_CONTEXT_SELECTION_MAX_BYTES} UTF-8 bytes`,
  ),
);
const SelectionContentUnavailableSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(240),
);
const SelectionBlockContextSchema = Schema.Struct({
  blockId: OfficeCanonicalIdSchema,
  text: SelectionTextSchema,
});
const SelectionCellContextSchema = Schema.Struct({
  blockId: OfficeCanonicalIdSchema,
  rowIndex: OfficeDocumentTableIndexSchema,
  cellIndex: OfficeDocumentTableIndexSchema,
  text: SelectionTextSchema,
});
const OfficeExcerptTextSchema = Schema.String.check(
  Schema.isMaxLength(APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES ||
      `Office excerpt text must not exceed ${APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES} UTF-8 bytes`,
  ),
);
const OfficeExcerptUnavailableReasonSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(240),
);

export const SurfaceSchema = Schema.Literals(["projects", "developer-console"]);
export const PanelSchema = Schema.Literals(["artifacts", "browser", "calendar", "diff", "files", "notes"]);
export const OfficeLocalEditStateSchema = Schema.Literals([
  "clean",
  "dirty",
  "conflict",
  "historical",
]);

export const OfficeExcerptSchema = Schema.Struct({
  kind: OfficeArtifactKindSchema,
  scope: BoundedLabelSchema,
  text: OfficeExcerptTextSchema,
  truncated: Schema.Boolean,
  unavailableReason: Schema.optionalKey(OfficeExcerptUnavailableReasonSchema),
}).check(
  Schema.makeFilter(
    (excerpt) =>
      excerpt.text.length > 0 ||
      excerpt.unavailableReason !== undefined ||
      "an Office excerpt must include bounded text or an unavailable reason",
  ),
  Schema.makeFilter(
    (excerpt) =>
      !excerpt.truncated ||
      excerpt.unavailableReason !== undefined ||
      "a truncated Office excerpt must include an explicit unavailable reason",
  ),
  Schema.makeFilter((excerpt) => {
    const excerptBytes =
      utf8ByteLength(excerpt.scope) +
      utf8ByteLength(excerpt.text) +
      (excerpt.unavailableReason ? utf8ByteLength(excerpt.unavailableReason) : 0);
    return (
      excerptBytes <= APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES ||
      `Office excerpt content must not exceed ${APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES} UTF-8 bytes`
    );
  }),
);


const OpenNotePreviewSchema = Schema.Struct({
  noteId: CanonicalIdentifierSchema,
  title: BoundedLabelSchema,
  preview: Schema.String.check(Schema.isMaxLength(240)),
});

const OpenNoteBodySchema = Schema.String.check(
  Schema.isMaxLength(APP_CONTEXT_NOTE_BODY_MAX_BYTES),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= APP_CONTEXT_NOTE_BODY_MAX_BYTES ||
      `open note body must not exceed ${APP_CONTEXT_NOTE_BODY_MAX_BYTES} UTF-8 bytes`,
  ),
);

export const OpenNoteContextSchema = Schema.Struct({
  noteId: CanonicalIdentifierSchema,
  title: BoundedLabelSchema,
  body: OpenNoteBodySchema,
  truncated: Schema.Boolean,
  recent: Schema.Array(OpenNotePreviewSchema).check(Schema.isMaxLength(8)),
});

export const ConnectionSummarySchema = Schema.Struct({
  provider: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  scope: Schema.Literals(["personal", "workspace"]),
  status: Schema.Literals(["connected", "expired", "pending", "error"]),
  displayName: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(160))),
  capabilities: Schema.Array(BoundedCapabilitySchema).check(Schema.isMaxLength(64)),
});

function supportsOfficeLocalEditState(kind: string | undefined): boolean {
  return (
    kind === "document" ||
    kind === "presentation" ||
    kind === "spreadsheet" ||
    kind === "pdf" ||
    kind === "markdown"
  );
}

export const ArtifactContextSchema = Schema.Struct({
  artifactId: ArtifactIdSchema,
  revisionId: Schema.optionalKey(ArtifactRevisionIdSchema),
  title: BoundedLabelSchema,
  kind: Schema.optionalKey(BoundedCapabilitySchema),
  officeLocalEditState: Schema.optionalKey(OfficeLocalEditStateSchema),
  officeExcerpt: Schema.optionalKey(OfficeExcerptSchema),
}).check(
  Schema.makeFilter((artifact) => {
    if (artifact.officeLocalEditState === undefined) return true;
    return (
      supportsOfficeLocalEditState(artifact.kind) ||
      "Office local edit state requires an Office artifact kind"
    );
  }),
  Schema.makeFilter((artifact) => {
    if (artifact.officeExcerpt === undefined) return true;
    return (
      (artifact.revisionId !== undefined && artifact.kind === artifact.officeExcerpt.kind) ||
      "Office excerpt kind requires an exact matching revision-scoped Office artifact"
    );
  }),
);

export const SelectionContextSchema = Schema.Struct({
  kind: Schema.Literals(["text", "code"]),
  text: SelectionTextSchema,
  start: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
  end: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
  target: Schema.optionalKey(OfficeSelectionTargetSchema),
  block: Schema.optionalKey(SelectionBlockContextSchema),
  cell: Schema.optionalKey(SelectionCellContextSchema),
  contentUnavailable: Schema.optionalKey(SelectionContentUnavailableSchema),
}).check(
  Schema.makeFilter(
    (selection) =>
      selection.start === undefined ||
      selection.end === undefined ||
      selection.start <= selection.end ||
      "selection start must not exceed selection end",
  ),
  Schema.makeFilter((selection) => {
    if (selection.target !== undefined) return true;
    return (
      (selection.block === undefined &&
        selection.cell === undefined &&
        selection.contentUnavailable === undefined) ||
      "selection content availability requires an Office target"
    );
  }),
  Schema.makeFilter(
    (selection) =>
      selection.target === undefined ||
      (selection.kind === "text" && selection.start === undefined && selection.end === undefined) ||
      "Office selection coordinates belong to the canonical target",
  ),
  Schema.makeFilter(
    (selection) =>
      selection.target === undefined ||
      selection.text.length > 0 ||
      selection.cell !== undefined ||
      selection.contentUnavailable !== undefined ||
      "an Office selection must include bounded visible text or an unavailable reason",
  ),
  Schema.makeFilter(
    (selection) =>
      selection.block === undefined ||
      selection.cell === undefined ||
      "an Office selection cannot include both block and table-cell content",
  ),
  Schema.makeFilter((selection) => {
    if (selection.block === undefined) return true;
    if (selection.target?.kind !== "document-text" && selection.target?.kind !== "markdown-text") {
      return "only document and Markdown targets can include selected block content";
    }
    return (
      selection.block.blockId === selection.target.blockId ||
      "selected block content must match the canonical target block id"
    );
  }),
  Schema.makeFilter((selection) => {
    if (selection.cell === undefined) return true;
    if (selection.target?.kind !== "document-table-cell-text") {
      return "only a document table-cell target can include selected cell content";
    }
    return (
      (selection.cell.blockId === selection.target.blockId &&
        selection.cell.rowIndex === selection.target.rowIndex &&
        selection.cell.cellIndex === selection.target.cellIndex) ||
      "selected cell content must match the canonical table-cell coordinates"
    );
  }),
  Schema.makeFilter((selection) => {
    if (selection.cell === undefined || selection.target?.kind !== "document-table-cell-text") {
      return true;
    }
    return (
      (selection.target.end <= selection.cell.text.length &&
        selection.text ===
          selection.cell.text.slice(selection.target.start, selection.target.end)) ||
      "selected table-cell text must match the exact bounded cell content"
    );
  }),
  Schema.makeFilter(
    (selection) =>
      selection.target?.kind !== "document-table-cell-text" ||
      selection.cell !== undefined ||
      selection.contentUnavailable !== undefined ||
      "a document table-cell target requires complete cell content or an unavailable reason",
  ),
  Schema.makeFilter((selection) => {
    const selectedContentBytes =
      utf8ByteLength(selection.text) +
      (selection.block ? utf8ByteLength(selection.block.text) : 0) +
      (selection.cell ? utf8ByteLength(selection.cell.text) : 0);
    return (
      selectedContentBytes <= APP_CONTEXT_SELECTION_MAX_BYTES ||
      `selected visible content must not exceed ${APP_CONTEXT_SELECTION_MAX_BYTES} UTF-8 bytes`
    );
  }),
);

export const RuntimeContextSchema = Schema.Struct({
  target: Schema.optionalKey(Schema.Literals(["local", "cloud"])),
  relativeFile: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(1_024))),
  branch: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(240))),
  terminalCwdAlias: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(1_024))),
  previewUrl: Schema.optionalKey(HttpUrlSchema),
});

export const ViewerContextSchema = Schema.Struct({
  activePanel: PanelSchema,
});

function officeSelectionArtifactKind(
  target: typeof OfficeSelectionTargetSchema.Type,
): OfficeArtifactKind {
  switch (target.kind) {
    case "document-text":
    case "document-table-cell-text":
      return "document";
    case "presentation-elements":
      return "presentation";
    case "spreadsheet-range":
      return "spreadsheet";
    case "markdown-text":
      return "markdown";
    case "pdf-text":
      return "pdf";
    default: {
      const exhaustiveTarget: never = target;
      return exhaustiveTarget;
    }
  }
}

export const AppContextSchema = Schema.Struct({
  version: Schema.Literal(1),
  requestId: RequestIdSchema,
  surface: SurfaceSchema,
  route: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(1_024)),
  workspaceId: WorkspaceIdSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  threadId: Schema.optionalKey(ThreadIdSchema),
  environmentId: Schema.optionalKey(EnvironmentIdSchema),
  apiProjectId: Schema.optionalKey(ApiProjectIdSchema),
  apiEnvironmentId: Schema.optionalKey(ApiEnvironmentIdSchema),
  actor: ActorSchema,
  artifact: Schema.optionalKey(ArtifactContextSchema),
  selection: Schema.optionalKey(SelectionContextSchema),
  runtime: Schema.optionalKey(RuntimeContextSchema),
  viewer: Schema.optionalKey(ViewerContextSchema),
  openNote: Schema.optionalKey(OpenNoteContextSchema),
  connections: Schema.Array(ConnectionSummarySchema).check(Schema.isMaxLength(100)),
  clientCapabilities: Schema.Array(BoundedCapabilitySchema).check(Schema.isMaxLength(64)),
  locale: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(32)),
  timeZone: IanaTimeZoneSchema,
  capturedAt: IsoDateTimeSchema,
}).check(
  Schema.makeFilter(
    (context) =>
      context.actor.workspaceId === context.workspaceId ||
      "Actor context must match the addressed workspace",
  ),
  Schema.makeFilter((context) => {
    if (context.surface === "projects") {
      return (
        (context.apiProjectId === undefined && context.apiEnvironmentId === undefined) ||
        "API administration identifiers cannot enter project context"
      );
    }
    return (
      (context.projectId === undefined &&
        context.threadId === undefined &&
        context.environmentId === undefined &&
        context.artifact === undefined &&
        context.selection === undefined &&
        context.runtime === undefined &&
        context.viewer === undefined &&
        context.openNote === undefined) ||
      "Developer Console context cannot carry project runtime or artifact authority"
    );
  }),
  Schema.makeFilter(
    (context) =>
      context.apiEnvironmentId === undefined ||
      context.apiProjectId !== undefined ||
      "API environment context requires its API project",
  ),
  Schema.makeFilter(
    (context) =>
      context.environmentId === undefined ||
      context.projectId !== undefined ||
      "Runtime environment context requires its workspace Project",
  ),

  Schema.makeFilter((context) => {
    if (context.openNote === undefined) return true;
    return (
      (context.surface === "projects" &&
        context.projectId !== undefined &&
        context.viewer?.activePanel === "notes") ||
      "Open note context requires the Notes panel in a project"
    );
  }),
  Schema.makeFilter((context) => {
    if (
      context.artifact?.officeLocalEditState === undefined &&
      context.artifact?.officeExcerpt === undefined
    ) {
      return true;
    }
    const projectsSurface =
      context.surface === "projects" && context.projectId !== undefined;
    if (context.viewer?.activePanel === "artifacts") {
      return (
        (projectsSurface &&
          (context.artifact?.officeExcerpt === undefined ||
            context.artifact.revisionId !== undefined) &&
          (context.artifact?.officeLocalEditState === undefined ||
            context.artifact.revisionId !== undefined)) ||
        "Office view projection requires an active project artifact view"
      );
    }
    return (
      (projectsSurface &&
        context.viewer?.activePanel === "files" &&
        context.runtime?.relativeFile !== undefined &&
        context.artifact?.officeExcerpt === undefined) ||
      "Office view projection requires an active project artifact view"
    );
  }),
  Schema.makeFilter((context) => {
    const excerpt = context.artifact?.officeExcerpt;
    if (excerpt === undefined || context.selection === undefined) return true;
    const visibleSelectionBytes =
      utf8ByteLength(context.selection.text) +
      (context.selection.block ? utf8ByteLength(context.selection.block.text) : 0) +
      (context.selection.cell ? utf8ByteLength(context.selection.cell.text) : 0);
    const excerptBytes =
      utf8ByteLength(excerpt.scope) +
      utf8ByteLength(excerpt.text) +
      (excerpt.unavailableReason ? utf8ByteLength(excerpt.unavailableReason) : 0);
    return (
      visibleSelectionBytes + excerptBytes <= APP_CONTEXT_OFFICE_EXCERPT_MAX_BYTES ||
      "Office excerpt and selection content must share one 8 KiB UTF-8 budget"
    );
  }),
  Schema.makeFilter((context) => {
    const target = context.selection?.target;
    if (target === undefined) return true;
    return (
      (context.surface === "projects" &&
        context.projectId !== undefined &&
        context.viewer?.activePanel === "artifacts" &&
        context.artifact?.revisionId !== undefined &&
        context.artifact.kind === officeSelectionArtifactKind(target)) ||
      "Office selection targets require an exact revision-scoped artifact view"
    );
  }),
);

export const decodeAppContext = Schema.decodeUnknownSync(
  AppContextSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectionSummary = Schema.decodeUnknownSync(
  ConnectionSummarySchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAppContext = Schema.encodeSync(AppContextSchema, PapersFlowBoundaryParseOptions);

export type Surface = typeof SurfaceSchema.Type;
export type Panel = typeof PanelSchema.Type;
export type OfficeLocalEditState = typeof OfficeLocalEditStateSchema.Type;
export type OfficeExcerpt = typeof OfficeExcerptSchema.Type;
export type OfficeExcerptEncoded = typeof OfficeExcerptSchema.Encoded;
export type ConnectionSummary = typeof ConnectionSummarySchema.Type;
export type ConnectionSummaryEncoded = typeof ConnectionSummarySchema.Encoded;
export type ArtifactContext = typeof ArtifactContextSchema.Type;
export type SelectionContext = typeof SelectionContextSchema.Type;
export type SelectionContextEncoded = typeof SelectionContextSchema.Encoded;
export type RuntimeContext = typeof RuntimeContextSchema.Type;
export type ViewerContext = typeof ViewerContextSchema.Type;
export type OpenNoteContext = typeof OpenNoteContextSchema.Type;
export type AppContext = typeof AppContextSchema.Type;
export type AppContextEncoded = typeof AppContextSchema.Encoded;

export function sanitizeAppContext(input: unknown): AppContext {
  const context = decodeAppContext(input);
  return {
    ...context,
    route: context.route.replace(/[?#].*$/u, ""),
  };
}
